import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Globe, Shield, Stethoscope, Calendar, Wallet, Star, Zap, Clock,
  CheckCircle, XCircle, ArrowRight, Heart, IndianRupee, Users, TrendingUp,
  Smartphone, Lock, Award, BarChart3, MapPin, Camera, Crown, UserX,
  MessageCircle, Mail, Share2, ShieldCheck, KeyRound, Bell,
  Newspaper, QrCode, Wifi, Phone, Search, Download, ClipboardList, Sparkles, UserPlus, Printer, Coins,
  Copy, Check
} from 'lucide-react';
import { COMPARE_ROWS } from '@/data/compareRows';
import InstallQR from '@/components/InstallQR';

const DOC_IMG = "https://images.unsplash.com/photo-1642975967602-653d378f3b5b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxJbmRpYW4lMjBkb2N0b3IlMjB1c2luZyUyMHRhYmxldCUyMHRlY2hub2xvZ3klMjBtb2Rlcm4lMjBjbGluaWN8ZW58MHx8fHwxNzc2MTU3NDEzfDA&ixlib=rb-4.1.0&q=85";

/**
 * Peer-to-peer WhatsApp / copy-link share widget for doctors who just read the
 * brochure. Message is crafted for doctor → doctor tone (not patient-facing).
 * No platform SMS cost — rides on the doctor's own WhatsApp identity.
 */
const PEER_SHARE_MSG = "Hey doc — I just started using DocSite for my practice. My own booking website + SEO, free on the Gold plan. Promising future for us! Check it out 👉";
const PEER_SHARE_URL = "https://docsite.care/brochure";
const PEER_SHARE_FULL = `${PEER_SHARE_MSG} ${PEER_SHARE_URL}`;

function DoctorPeerShare() {
  const [copied, setCopied] = React.useState(false);
  const waHref = `https://wa.me/?text=${encodeURIComponent(PEER_SHARE_FULL)}`;
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(PEER_SHARE_FULL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch (e) {
      // Fallback — select + execCommand for old WebKit
      const ta = document.createElement('textarea');
      ta.value = PEER_SHARE_FULL;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); setCopied(true); setTimeout(() => setCopied(false), 2200); } catch (_) {}
      document.body.removeChild(ta);
    }
  };
  return (
    <div className="mt-6 rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-sky-50 p-5 md:p-6" data-testid="peer-share-card">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex-1 min-w-[220px]">
          <p className="text-[10px] font-bold text-emerald-700 uppercase tracking-[0.18em] mb-1.5 flex items-center gap-1.5">
            <UserPlus size={12} /> Share with a doctor friend
          </p>
          <p className="text-sm text-slate-700 leading-relaxed">
            "<em>I just started using it. Promising future for us!</em>" — send this note (and a link to this page) to a peer over WhatsApp in one tap.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <a
            href={waHref}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500 text-white text-xs font-bold hover:bg-emerald-600 transition-colors"
            data-testid="peer-share-whatsapp"
          >
            <MessageCircle size={13} /> Share on WhatsApp
          </a>
          <button
            type="button"
            onClick={onCopy}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-slate-200 text-slate-700 text-xs font-bold hover:border-emerald-300 hover:text-emerald-700 transition-colors"
            data-testid="peer-share-copy"
          >
            {copied ? <><Check size={13} className="text-emerald-600" /> Copied!</> : <><Copy size={13} /> Copy message + link</>}
          </button>
        </div>
      </div>
      <p className="mt-3 text-[11px] text-slate-400 italic leading-relaxed" data-testid="peer-share-preview">
        Preview: "{PEER_SHARE_MSG} {PEER_SHARE_URL}"
      </p>
    </div>
  );
}


const FREE_PLAN_FEATURES = [
  { icon: Globe, title: 'Personal Doctor Website', desc: 'Professional branded page at docsite.com/dr/your-name. Live in minutes.' },
  { icon: Calendar, title: 'Multi-Location, Multi-Service Scheduling', desc: 'Declare as many time-blocks per day as you want. Each block can mix OPD, Consulting, Surgeries, or Minor Procedures — patients only see slots for the service they need.' },
  { icon: Clock, title: '15-Minute Slot Engine', desc: 'Your declared windows are auto-fragmented into 15-minute bookable slots. Non-contiguous blocks (e.g. 9–11 AM + 4–7 PM at the same clinic) are fully supported.' },
  { icon: MapPin, title: 'Unlimited Practice Locations', desc: 'List all your clinics and hospitals with addresses and phone numbers.' },
  { icon: ShieldCheck, title: 'Structured Cancellation Protocol', desc: 'When you need to cancel, pick a reason code (Emergency Surgery, Clinic Overrun, Personal Emergency…). Patient is notified by email + push. Every action is audit-logged. 1-hour buffer prevents last-minute chaos.' },
  { icon: QrCode, title: 'Patients Can Scan & Save Your Clinic', desc: 'When patients scan your DocSite link (or, for TurboCharge doctors, your printed Clinic Kit QR poster), we auto-save your clinic to their "My Trusted Medical Clinics" list. No app download, no typing. Next booking is one tap away.' },
  { icon: Camera, title: 'Clinic Monogram Branding', desc: 'Auto-generated specialty-coloured monogram when you have no photo — looks crisp everywhere, never blank.' },
  { icon: Download, title: 'Install as an App (PWA)', desc: 'Patients get a discreet "Install" prompt on their phone. Opens offline, no app store, maskable Android icon, branded splash. Feels native.' },
  { icon: BarChart3, title: 'Website Readiness Score', desc: 'Gamified 0-100 score guides you to complete your profile.' },
  { icon: CheckCircle, title: 'Consult Completed Tracking', desc: 'Mark appointments as completed, track patient attendance.' },
  { icon: Share2, title: 'Share Your Website Instantly', desc: 'One-tap sharing via WhatsApp, native share, or copy link. Grow your patient base.' },
  { icon: MessageCircle, title: 'WhatsApp CTA on Your Website', desc: 'Patients can message you directly on WhatsApp from your website.' },
  { icon: Lock, title: 'Legal Protection Built-In', desc: 'Mandatory consent, liability disclaimers, IP/audit logging, NMC self-declaration banner.' },
  { icon: Smartphone, title: 'PWA Offline Browsing', desc: 'Patients browse your website even without internet.' },
  { icon: UserX, title: 'No AI Assistance — Author Every Word Yourself', desc: 'Free tier deliberately ships zero AI drafting. You write your bio, procedures and articles in your own clinical voice. This protects DocSite\'s content quality and gives patients the real you. Coffee Premium adds a one-time AI on-ramp; TurboCharge adds monthly AI pulls.' },
];

const PREMIUM_PLAN_FEATURES = [
  { icon: Sparkles, title: 'One-Time AI On-Ramp · 3 Lifetime Pulls', desc: 'Let GPT seed your bio, procedures and FAQ sections — typically used once. After 3 pulls, you continue manually. Perfect for doctors who want a starting point without committing to TurboCharge yet.' },
  { icon: MapPin, title: 'Clinic Display + Embedded Map', desc: 'Show your clinic display name, address, pincode, and a privacy-safe OpenStreetMap embed on your public page. No Google tracking, no API keys.' },
  { icon: Stethoscope, title: 'Per-Procedure Price Ranges', desc: 'Set indicative min/max pricing per procedure. Shown publicly under each service — patients get honest expectations, you get fewer "how much" calls.' },
  { icon: MessageCircle, title: 'Patient ↔ Doctor Booking Chat', desc: 'Per-booking thread, hard-capped at 3 messages per side. Enough to clarify, never to overrun. Works offline.' },
  { icon: Wallet, title: 'Doctor Wallet & Appointment Codes', desc: 'Unique 6-digit appointment code for each doctor. Wallet & IVR booking coming soon.' },
  { icon: UserX, title: 'Did Not Attend (DNA) Tracking', desc: 'Mark no-shows, auto-refund wallet, patient reliability scoring.' },
  { icon: Zap, title: 'Priority Listing & Premium Badge', desc: 'Stand out with a premium badge on the platform.' },
  { icon: Lock, title: 'Encrypted Data Export', desc: 'Fernet (AES-128) encrypted backup of your interaction data, sealed with your passphrase. Always accessible — even if DocSite disappears.' },
  { icon: Mail, title: 'Email Notifications', desc: 'Automated welcome emails, payment receipts, and subscription reminders via Gmail. Patients get cancellation emails with the reason.' },
  { icon: TrendingUp, title: 'IVR Missed-Call Booking', desc: 'Coming Soon — Patients book via missed call to your number.' },
];

const TURBO_FEATURES = [
  { icon: Sparkles, title: '2 AI Pulls per Month — You Decide What They Power', desc: 'Turbo gives you 2 monthly AI pulls to spend across SEO articles, per-procedure landing pages, AI case-study check, AI address parse, or smart procedure blending. Nothing auto-fires. Zero surprise token burn. Full control.' },
  { icon: Newspaper, title: 'AI-Written SEO Articles, You Own', desc: 'Up to 2 SEO-ready articles per month about your specialty in your city. You review, edit, one-click publish. Every word belongs to you — full append-only audit trail.' },
  { icon: Stethoscope, title: 'Per-Procedure SEO Landing Pages', desc: 'Explicitly enable any procedure to get its own long-form, Google-indexed page. Once generated, it\'s cached forever — patients and crawlers always get a fast page.' },
  { icon: QrCode, title: 'Clinic Kit — Printable QR Poster (Patient Auto-Save)', desc: 'One-click A4 poster PDF with your branded QR code and NMC registration number. When a patient scans it, DocSite instantly auto-saves your clinic to their "My Trusted Medical Clinics" list and redirects them to your booking page — even if they\'re new to DocSite. Zero friction, zero typing.' },
  { icon: Wifi, title: 'Works Offline After One Visit', desc: 'Your DocSite page is cached on the patient\'s phone. Even with zero network — your website opens instantly. Built for real India.' },
  { icon: Phone, title: 'Offline "Save Contact" QR', desc: 'A secondary vCard QR on the poster saves your number, WhatsApp, and clinic addresses directly to the patient\'s phone — 100% offline.' },
  { icon: TrendingUp, title: 'Turbo Visibility Accelerator', desc: '5 guided link placements with pre-written text and one-tap copy. Tells Google you\'re a real, practicing doctor. Your visibility compounds every month — 5x faster than doing nothing.' },
  { icon: Globe, title: 'Auto-SEO Landing Pages', desc: 'Your practice automatically appears on specialty+city pages like "Cardiologists in Mumbai." New pages auto-create as doctors join. Zero effort, compounding organic visibility.' },
  { icon: Newspaper, title: 'Medical Insights Hub', desc: 'Your articles appear on a public knowledge hub alongside other specialists. Patients discover you through the expertise you share — not through ads.' },
  { icon: BarChart3, title: 'Patient Referral Tracking', desc: 'Patients share your profile with tracked links. Every click and appointment attributed. Your best patients become your organic growth engine.' },
  { icon: QrCode, title: '"Save My Details" Live Widget', desc: 'A floating QR pill on your public website. One tap opens a branded modal with two QR codes — save contact offline, or open your site on another device.' },
  { icon: BarChart3, title: 'Clinic-QR Lead Attribution', desc: 'Scans from your clinic poster show separately in My Leads — so you know exactly which patients walked in and booked online.' },
  { icon: Shield, title: 'Internal Cross-Linking Engine', desc: 'Your procedures, articles, and profile automatically interlink. Google sees a connected web of expertise — your page authority grows organically.' },
  { icon: ClipboardList, title: 'Cancellation & Audit Dashboard', desc: 'Every appointment action is logged. See why patients cancel, why you had to cancel, and reliability trends — so you can spot overbooking before it hurts your ratings.' },
];

const PATIENT_BENEFITS = [
  { icon: CheckCircle, title: 'Direct Doctor Access', desc: 'Visit your doctor\'s own website — no aggregator platform in between. No commercial rankings. The relationship is yours.' },
  { icon: QrCode, title: 'Scan & Save in One Tap', desc: 'Scan your doctor\'s QR poster at the clinic — we auto-save the clinic to your "My Trusted Medical Clinics" list. No typing, no searching. Next booking is one tap.' },
  { icon: UserPlus, title: 'Zero-Touch Onboarding', desc: 'New to DocSite? Scan the QR and we\'ll pre-connect the doctor during signup. Your clinic is saved before you even finish registering.' },
  { icon: MapPin, title: 'One-Tap Directions', desc: 'Every saved clinic card has a "Get Directions" button that opens Google Maps with the exact clinic address. Zero setup on your side.' },
  { icon: Clock, title: 'Real-Time Availability by Service', desc: 'See exactly when your doctor is available for what — OPD, Consulting, Surgery slots are filtered separately. Book directly on their schedule.' },
  { icon: Bell, title: 'You\'re Told Why, Not Ghosted', desc: 'If a doctor has to cancel, you get an email + push with a structured reason (Emergency Surgery, Personal Emergency, etc.). 1-hour minimum notice is enforced by the platform, not optional.' },
  { icon: Download, title: 'Install on Your Phone', desc: 'Add the DocSite app to your home screen in one tap. No app store. Works offline. Opens instantly.' },
  { icon: Star, title: 'Honest Clinical Feedback', desc: 'Ratings based on actual clinical outcomes — not bought reviews or inflated scores. Objective, not subjective.' },
  { icon: MessageCircle, title: 'WhatsApp Your Doctor', desc: 'Message your doctor directly on WhatsApp from their website. Coordinate before your appointment.' },
  { icon: Smartphone, title: 'Works on Any Phone', desc: 'No app download needed. Works in your browser, even offline. Built for real India — Tier 1, 2, and 3 cities.' },
  { icon: ShieldCheck, title: 'Self-Declared Credentials', desc: 'NMC/State Medical Council registrations displayed as declared by the doctor under a signed truthful-declaration consent. Find doctors based on real qualifications — not paid placements.' },
  { icon: Search, title: 'Find Specialists Near You', desc: 'Browse doctors by specialty and city. Auto-generated pages with no ads, no paid rankings — just doctors publishing under their own accountability.' },
  { icon: Heart, title: 'Refer Doctors You Trust', desc: 'Share your doctor\'s profile with friends via tracked referral links. Help others find trustworthy care.' },
];

const INDIA_COMPETITORS = [
  { name: 'Company A', region: 'India', price: 'Rs. 5,000-15,000/mo', features: ['Doctor listing (no own website)', 'Basic scheduling', 'Tech fee per new patient', 'Reviews (purchasable)'], missing: ['No own website', 'No AI tools', 'No SMS OTP 2FA', 'No data export'] },
  { name: 'Company B', region: 'India', price: 'Free + Commission', features: ['Q&A platform listing', 'Basic profile', 'Commission on consults'], missing: ['No own website', 'No scheduling', 'No WhatsApp CTA', 'No AI'] },
];

const GLOBAL_COMPETITORS = [
  { name: 'Company C', region: 'USA', price: '$35-180/booking', features: ['Patient search listing', 'Online scheduling', 'Reviews'], missing: ['No own website', 'No AI', 'No email alerts'] },
  { name: 'Company D', region: 'Europe', price: '€129/mo (~Rs. 12,000)', features: ['Scheduling', 'Teleconsultation', 'EU-only'], missing: ['No own website', 'No AI', 'No offline'] },
  { name: 'Company E', region: 'USA', price: '$99-399/mo', features: ['Website builder', 'SEO', 'Patient engagement'], missing: ['No AI procedures', 'No WhatsApp', 'Custom quotes only'] },
  { name: 'Company F', region: 'USA', price: '$29-99/mo + add-ons', features: ['EHR + scheduling', 'Telehealth'], missing: ['No own website', 'No AI', 'No share URL'] },
];

export default function ProductBrochure() {
  return (
    <div style={{ fontFamily: 'Manrope, sans-serif' }}>
      <Helmet>
        <title>DocSite Product Brochure — Gold, Coffee Premium & TurboCharge Plans</title>
        <meta name="description" content="Compare DocSite plans: Gold (Free for the first 200 doctors), Coffee Premium (Rs. 299/mo), and TurboCharge (Rs. 999/mo). AI-powered SEO for doctors. No commission on bookings, ever." />
      </Helmet>
      {/* ====== SECTION 1: DOCTOR HERO ====== */}
      <section className="relative bg-gradient-to-br from-sky-50 via-white to-slate-50 overflow-hidden" data-testid="brochure-hero">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #0284C7 1px, transparent 0)', backgroundSize: '32px 32px' }} />
        <div className="relative w-full px-6 md:px-12 lg:px-20 xl:px-28 py-10 md:py-14 lg:min-h-[calc(100vh-8rem)] flex flex-col">
          <div className="flex-1 min-h-0">
            <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-sky-100 border border-sky-200 text-sky-700 text-xs font-bold mb-6">
                <Zap size={14} /> A PRODUCT OF CHAINX TECH LABS
              </div>
              <h1 className="font-medium text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(2.5rem, 4vw, 4.75rem)', lineHeight: 1.05 }}>
                Your Practice.<br />
                <span className="text-sky-600">Your Patient.</span><br />
                Your Brand.
              </h1>
              <p className="text-slate-600 mt-6 leading-relaxed max-w-2xl" style={{ fontSize: 'clamp(1.05rem, 1.15vw, 1.3rem)' }}>
                DocSite is the platform that brings the doctor–patient conversation back to the specialist. Your real medical expertise — not ad spend, not aggregator algorithms, not hospital marketing — determines how patients find and reach you. AI-powered SEO, patient referral tracking, and auto-generated specialty hubs that grow your organic presence. No commercial exploitation. No middlemen.
              </p>
              <div className="flex flex-wrap gap-4 mt-8">
                <Link to="/register?as=doctor&from=brochure" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-sky-600 text-white font-semibold text-sm hover:bg-sky-700 transition-colors" data-testid="brochure-cta-register">
                  Start Free <ArrowRight size={16} />
                </Link>
                <div className="flex items-center gap-2 px-6 py-3.5 rounded-full border border-slate-300 text-slate-700 text-sm">
                  <IndianRupee size={16} className="text-emerald-500" />
                  <span>Gold tier <strong className="text-slate-900">Free</strong> for the first 200 doctors · Premium from <strong className="text-slate-900">Rs. 299/month</strong></span>
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-3">Free Gold seats are first-come, first-served and locked once claimed. See <Link to="/terms/doctor" className="text-sky-700 underline decoration-dotted">Terms &amp; Conditions § Free Plan Continuity</Link> for the long-form.</p>

              {/* Install-the-app QR — lets a doctor show this on their laptop/slide so a peer can install DocSite instantly */}
              <InstallQR className="mt-6" testId="brochure-install-qr" />
            </div>
            <div className="hidden md:block">
              <img src={DOC_IMG} alt="Doctor with technology" className="rounded-3xl shadow-2xl shadow-sky-200/50 w-full max-w-md mx-auto object-cover aspect-[3/4]" />
            </div>
          </div>
          </div>

          {/* ====== Trust row + JUMP-TO PILLS — anchored to bottom of viewport so doctors see "there's more below" ====== */}
          <div className="mt-6 pt-4 border-t border-slate-200/80 space-y-4">
            <div className="flex flex-wrap items-center gap-x-8 gap-y-2 text-slate-600 font-medium" style={{ fontSize: 'clamp(0.95rem, 1.05vw, 1.2rem)' }} data-testid="brochure-trust-row">
              <span className="flex items-center gap-2"><CheckCircle size={20} className="text-emerald-500" /> Free Gold tier</span>
              <span className="flex items-center gap-2"><CheckCircle size={20} className="text-emerald-500" /> Premium ₹399/month</span>
              <span className="flex items-center gap-2"><CheckCircle size={20} className="text-emerald-500" /> 5-minute setup</span>
            </div>

            <nav aria-label="Brochure section shortcuts" data-testid="brochure-jump-nav">
            <p className="font-bold text-slate-500 uppercase tracking-[0.18em] mb-3 flex items-center gap-1.5" style={{ fontSize: 'clamp(0.7rem, 0.75vw, 0.85rem)' }}>
              <ArrowRight size={12} /> Jump to on this page
            </p>
            <div className="flex flex-wrap gap-2.5">
              <a href="#compare-plans" className="group inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-amber-50 border-2 border-amber-200 text-amber-800 font-semibold hover:bg-amber-500 hover:text-white hover:border-amber-500 transition-colors" style={{ fontSize: 'clamp(0.85rem, 0.95vw, 1.05rem)' }} data-testid="jump-compare">
                <span aria-hidden="true">🪙</span> Compare plans
              </a>
              <a href="#turbocharge" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-emerald-50 border-2 border-emerald-200 text-emerald-800 font-semibold hover:bg-emerald-600 hover:text-white hover:border-emerald-600 transition-colors" style={{ fontSize: 'clamp(0.85rem, 0.95vw, 1.05rem)' }} data-testid="jump-turbo">
                <Zap size={14} /> TurboCharge details
              </a>
              <a href="#competitor-compare" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-sky-50 border-2 border-sky-200 text-sky-800 font-semibold hover:bg-sky-600 hover:text-white hover:border-sky-600 transition-colors" style={{ fontSize: 'clamp(0.85rem, 0.95vw, 1.05rem)' }} data-testid="jump-competitors">
                <BarChart3 size={14} /> vs other platforms
              </a>
              <a href="#for-patients" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-rose-50 border-2 border-rose-200 text-rose-800 font-semibold hover:bg-rose-600 hover:text-white hover:border-rose-600 transition-colors" style={{ fontSize: 'clamp(0.85rem, 0.95vw, 1.05rem)' }} data-testid="jump-patients">
                <Heart size={14} /> For patients
              </a>
              <a href="#security" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-slate-100 border-2 border-slate-200 text-slate-700 font-semibold hover:bg-slate-900 hover:text-white hover:border-slate-900 transition-colors" style={{ fontSize: 'clamp(0.85rem, 0.95vw, 1.05rem)' }} data-testid="jump-security">
                <ShieldCheck size={14} /> Security & trust
              </a>
              <Link to="/business-case" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white border-2 border-slate-200 text-slate-600 font-semibold hover:border-slate-900 hover:text-slate-900 transition-colors" style={{ fontSize: 'clamp(0.85rem, 0.95vw, 1.05rem)' }} data-testid="jump-business-case">
                <Printer size={14} /> Printable 1-pager
              </Link>
            </div>
          </nav>
          </div>
        </div>
      </section>

      {/* ====== SECTION 1.5: PLANS AT A GLANCE ====== */}
      <section id="compare-plans" className="bg-white border-t border-slate-100 py-16 md:py-20 scroll-mt-20" data-testid="brochure-compare-table">
        <div className="w-full px-6 md:px-12 lg:px-20 xl:px-28">
          <div className="text-center mb-10">
            <p className="text-sky-600 text-xs font-bold uppercase tracking-widest mb-3">Compare Plans</p>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Decide in 10 seconds
            </h2>
            <p className="text-slate-500 mt-3 max-w-2xl mx-auto text-sm leading-relaxed">
              Every plan gets the foundation right. Premium and TurboCharge unlock growth levers. The last two columns show exactly how each feature impacts your <b>service quality</b> and your <b>patient acquisition/retention</b>.
            </p>
          </div>

          <div className="overflow-x-auto rounded-2xl border border-slate-200 shadow-sm bg-white">
            <table className="w-full text-sm" data-testid="compare-plans-table">
              <thead className="bg-slate-50">
                <tr>
                  <th scope="col" className="text-left px-5 py-4 font-semibold text-slate-600 text-xs uppercase tracking-wider sticky left-0 bg-slate-50 min-w-[220px]">Feature</th>
                  <th scope="col" className="px-4 py-4 text-center min-w-[110px]">
                    <p className="text-[10px] font-bold text-amber-600 uppercase tracking-widest">Gold</p>
                    <p className="text-base font-bold text-slate-900 mt-0.5" style={{ fontFamily: 'Outfit, sans-serif' }}>Free</p>
                    <p className="text-[9px] text-amber-700 font-medium mt-0.5">first 200 doctors · Rs. 99/mo after</p>
                  </th>
                  <th scope="col" className="px-4 py-4 text-center bg-sky-50/40 border-x border-sky-100 min-w-[140px]">
                    <p className="text-[10px] font-bold text-sky-600 uppercase tracking-widest">Coffee Premium</p>
                    <p className="text-base font-bold text-sky-700 mt-0.5" style={{ fontFamily: 'Outfit, sans-serif' }}>Rs. 299<span className="text-[10px] font-normal text-slate-400">/mo</span></p>
                  </th>
                  <th scope="col" className="px-4 py-4 text-center min-w-[120px]">
                    <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-widest">TurboCharge</p>
                    <p className="text-base font-bold text-emerald-700 mt-0.5" style={{ fontFamily: 'Outfit, sans-serif' }}>Rs. 999<span className="text-[10px] font-normal text-slate-400">/mo</span></p>
                  </th>
                  <th scope="col" className="hidden lg:table-cell px-4 py-4 text-left text-xs font-semibold uppercase tracking-wider border-l-2 border-amber-300 bg-gradient-to-b from-amber-100 to-amber-50 text-amber-800 min-w-[260px]">
                    <span className="inline-flex items-center gap-1.5">
                      <span className="text-lg leading-none" aria-hidden="true">🪙</span>
                      <span>For Doctors — Service Quality Impact</span>
                    </span>
                  </th>
                  <th scope="col" className="hidden lg:table-cell px-4 py-4 text-left text-xs font-semibold uppercase tracking-wider border-l-2 border-teal-300 bg-gradient-to-b from-teal-100 to-teal-50 text-teal-800 min-w-[260px]">
                    <span className="inline-flex items-center gap-1.5">
                      <span className="text-lg leading-none" aria-hidden="true">🪙</span>
                      <span>For Doctors — Patient Acquisition / Retention</span>
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {COMPARE_ROWS.map((row, i) => (
                  <React.Fragment key={row.label}>
                    <tr className={`border-t border-slate-100 ${i % 2 === 1 ? 'bg-slate-50/40' : ''}`} data-testid={`compare-row-${i}`}>
                      <th scope="row" className={`text-left px-5 py-3.5 font-medium text-slate-700 sticky left-0 ${i % 2 === 1 ? 'bg-slate-50/40' : 'bg-white'}`}>{row.label}</th>
                      <td className="px-4 py-3.5 text-center">
                        {row.free ? <CheckCircle size={18} className="text-emerald-500 mx-auto" aria-label="Included" /> : <span className="text-slate-300" aria-label="Not included">—</span>}
                      </td>
                      <td className="px-4 py-3.5 text-center bg-sky-50/30 border-x border-sky-100">
                        {row.premium ? <CheckCircle size={18} className="text-sky-600 mx-auto" aria-label="Included" /> : <span className="text-slate-300" aria-label="Not included">—</span>}
                      </td>
                      <td className="px-4 py-3.5 text-center">
                        {row.turbo ? <CheckCircle size={18} className="text-emerald-600 mx-auto" aria-label="Included" /> : <span className="text-slate-300" aria-label="Not included">—</span>}
                      </td>
                      {/* Impact columns — visible on lg+ screens inline with the row */}
                      <td className="hidden lg:table-cell px-4 py-3.5 text-xs leading-relaxed align-top border-l border-amber-200 bg-amber-50/40 text-amber-900/90">
                        {row.service_impact}
                      </td>
                      <td className="hidden lg:table-cell px-4 py-3.5 text-xs leading-relaxed align-top border-l border-teal-200 bg-teal-50/40 text-teal-900/90">
                        {row.acquisition_impact}
                      </td>
                    </tr>
                    {/* Mobile/tablet: show the two impact blurbs stacked below each row so doctors get the "why it matters" without horizontal scroll. */}
                    <tr className={`lg:hidden ${i % 2 === 1 ? 'bg-slate-50/40' : ''}`} data-testid={`compare-row-impact-${i}`}>
                      <td colSpan={4} className="px-5 pb-4 pt-0">
                        <div className="grid sm:grid-cols-2 gap-2 text-xs leading-relaxed">
                          <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-2.5">
                            <p className="text-[9px] font-bold text-amber-700 uppercase tracking-widest mb-1 flex items-center gap-1">
                              <Coins size={11} className="text-amber-600" /> Service Quality
                            </p>
                            <p className="text-amber-900/90">{row.service_impact}</p>
                          </div>
                          <div className="rounded-lg border border-teal-200 bg-teal-50/60 p-2.5">
                            <p className="text-[9px] font-bold text-teal-700 uppercase tracking-widest mb-1 flex items-center gap-1">
                              <Coins size={11} className="text-teal-700" /> Patient Acquisition / Retention
                            </p>
                            <p className="text-teal-900/90">{row.acquisition_impact}</p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
              <tfoot className="bg-slate-50 border-t border-slate-200">
                <tr>
                  <td className="px-5 py-4"></td>
                  <td className="px-4 py-4 text-center">
                    <Link to="/register?as=doctor&from=brochure" className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-amber-500 text-white text-xs font-bold hover:bg-amber-600 transition-colors" data-testid="compare-cta-free">
                      Start Free
                    </Link>
                  </td>
                  <td className="px-4 py-4 text-center bg-sky-50/40 border-x border-sky-100">
                    <Link to="/register?as=doctor&from=brochure" className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-sky-600 text-white text-xs font-bold hover:bg-sky-700 transition-colors" data-testid="compare-cta-premium">
                      Go Premium
                    </Link>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <a href="#turbocharge" className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700 transition-colors" data-testid="compare-cta-turbo">
                      See Turbo
                    </a>
                  </td>
                  <td className="hidden lg:table-cell px-4 py-4 border-l border-amber-200 bg-amber-50/40"></td>
                  <td className="hidden lg:table-cell px-4 py-4 border-l border-teal-200 bg-teal-50/40"></td>
                </tr>
              </tfoot>
            </table>
          </div>

          <p className="text-[11px] text-slate-400 text-center mt-4">
            Full details of each feature in the sections below. Prices in INR, per month. Cancel anytime.
          </p>

          {/* ====== TURBO EQUATION — the "bottom line" of every impact column above ====== */}
          <div className="mt-10 rounded-2xl bg-gradient-to-br from-emerald-900 via-emerald-800 to-teal-900 text-white px-6 py-8 md:px-10 md:py-10 shadow-xl relative overflow-hidden" data-testid="turbo-equation-banner">
            {/* subtle grain / glow */}
            <div className="absolute inset-0 opacity-[0.08] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, #fff 1px, transparent 1px), radial-gradient(circle at 80% 70%, #fff 1px, transparent 1px)', backgroundSize: '18px 18px, 22px 22px' }} aria-hidden="true" />
            <div className="relative text-center">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/20 border border-emerald-300/30 text-emerald-200 text-[10px] font-bold uppercase tracking-[0.2em] mb-5">
                <span aria-hidden="true">🪙</span> Under TurboCharge, the math works for you
              </span>
              <p className="text-emerald-100/80 text-xs md:text-sm mb-5 max-w-2xl mx-auto leading-relaxed">
                Every feature above rolls up into a single, compounding equation for your practice:
              </p>
              <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-3 font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <span className="text-xl md:text-3xl text-white" data-testid="equation-more-patients">More Patients</span>
                <span className="text-2xl md:text-4xl text-amber-300/90" aria-hidden="true">×</span>
                <span className="text-xl md:text-3xl text-white" data-testid="equation-loyalty">Loyalty</span>
                <span className="text-2xl md:text-4xl text-amber-300/90" aria-hidden="true">×</span>
                <span className="text-xl md:text-3xl text-white" data-testid="equation-outcomes">Better Health Outcomes</span>
              </div>
              <p className="text-emerald-200/70 text-[11px] md:text-xs mt-5 max-w-xl mx-auto italic leading-relaxed">
                Compounds month over month — without ad spend, without commissions, without losing your patients to aggregators.
              </p>
              <Link to="/register?as=doctor&from=brochure" className="inline-flex items-center gap-2 mt-6 px-6 py-2.5 rounded-full bg-white text-emerald-900 text-xs font-bold hover:bg-emerald-50 transition-colors" data-testid="equation-cta-turbo">
                Start Free → Upgrade to Turbo later
              </Link>
            </div>
          </div>

          {/* Peer-to-peer doctor WhatsApp share — zero-cost viral loop */}
          <DoctorPeerShare />

          {/* Printable business case for doctors to share with their hospital admin / spouse / accountant. */}
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3 text-xs">
            <Link
              to="/business-case"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-slate-900 text-white font-semibold hover:bg-slate-800 transition-colors"
              data-testid="open-business-case"
            >
              <Printer size={12} /> Get the printable Business Case
            </Link>
            <span className="text-slate-400">Hand it to your hospital admin or practice manager. Same data, printable on 1 page.</span>
          </div>
        </div>
      </section>

      {/* ====== SECTION 2: 3-COLUMN COMPARISON ====== */}
      <section id="competitor-compare" className="bg-white py-20 md:py-28 scroll-mt-20" data-testid="brochure-doctor-benefits">
        <div className="w-full px-6 md:px-12 lg:px-20 xl:px-28">
          <div className="text-center mb-16">
            <p className="text-sky-600 text-xs font-bold uppercase tracking-widest mb-3">For Doctors</p>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              See What You're Paying For — Everywhere Else
            </h2>
            <p className="text-slate-500 mt-4 max-w-2xl mx-auto text-sm leading-relaxed">
              Compare what India and global platforms charge vs what DocSite gives you for free — and what costs less than a cup of coffee.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-5">
            {/* COLUMN 1: COMPETITORS */}
            <div className="space-y-5">
              {/* India */}
              <div className="rounded-2xl border border-red-200 bg-red-50/30 p-5">
                <p className="text-[10px] font-bold text-red-500 uppercase tracking-widest mb-4">India — Industry Pricing</p>
                {INDIA_COMPETITORS.map(c => (
                  <div key={c.name} className="mb-4 last:mb-0">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm font-bold text-slate-900">{c.name} <span className="text-[9px] text-slate-400 font-normal">({c.region})</span></p>
                      <span className="text-xs font-bold text-red-600">{c.price}</span>
                    </div>
                    <div className="space-y-1">
                      {c.features.map(f => <p key={f} className="text-[10px] text-slate-600 flex items-center gap-1.5"><CheckCircle size={10} className="text-slate-400" /> {f}</p>)}
                      {c.missing.map(f => <p key={f} className="text-[10px] text-red-400 flex items-center gap-1.5"><XCircle size={10} /> {f}</p>)}
                    </div>
                  </div>
                ))}
              </div>

              {/* Global */}
              <div className="rounded-2xl border border-red-200 bg-red-50/30 p-5">
                <p className="text-[10px] font-bold text-red-500 uppercase tracking-widest mb-4">Global — Industry Pricing</p>
                {GLOBAL_COMPETITORS.map(c => (
                  <div key={c.name} className="mb-4 last:mb-0">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm font-bold text-slate-900">{c.name} <span className="text-[9px] text-slate-400 font-normal">({c.region})</span></p>
                      <span className="text-xs font-bold text-red-600">{c.price}</span>
                    </div>
                    <div className="space-y-1">
                      {c.features.map(f => <p key={f} className="text-[10px] text-slate-600 flex items-center gap-1.5"><CheckCircle size={10} className="text-slate-400" /> {f}</p>)}
                      {c.missing.map(f => <p key={f} className="text-[10px] text-red-400 flex items-center gap-1.5"><XCircle size={10} /> {f}</p>)}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* COLUMN 2: DOCSITE GOLD (FREE) */}
            <div className="rounded-2xl border-2 border-amber-300 bg-gradient-to-b from-amber-50/50 to-white p-6 relative">
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-amber-500 text-white text-[10px] font-bold">GOLD · FIRST 200 FREE</span>
              <div className="text-center mb-5 pt-2">
                <p className="text-[10px] font-bold text-amber-600 uppercase tracking-widest">DocSite</p>
                <h3 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Gold</h3>
                <p className="text-3xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Rs. 0</p>
                <p className="text-xs text-slate-500">Free for the first 200 doctors</p>
                <p className="text-[10px] text-amber-700 mt-1">Rs. 99/month after the free pool fills</p>
              </div>
              <div className="space-y-3">
                {FREE_PLAN_FEATURES.map(({ icon: Icon, title, desc }) => (
                  <div key={title} className="flex items-start gap-2">
                    <CheckCircle size={14} className="text-emerald-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs font-semibold text-slate-900">{title}</p>
                      <p className="text-[9px] text-slate-500">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-5 pt-4 border-t border-amber-200 text-center">
                <Link to="/register?as=doctor&from=brochure" className="inline-flex items-center gap-1.5 px-6 py-2.5 rounded-full bg-amber-500 text-white text-xs font-bold hover:bg-amber-600 transition-colors">
                  Start Free <ArrowRight size={14} />
                </Link>
              </div>
            </div>

            {/* COLUMN 3: DOCSITE COFFEE PREMIUM */}
            <div className="rounded-2xl border-2 border-sky-400 bg-gradient-to-b from-sky-50/50 to-white p-6 relative">
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-sky-600 text-white text-[10px] font-bold whitespace-nowrap">RECOMMENDED</span>
              <div className="text-center mb-5 pt-2">
                <p className="text-[10px] font-bold text-sky-600 uppercase tracking-widest">DocSite</p>
                <h3 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Coffee Premium</h3>
                <div className="flex items-baseline justify-center gap-2 mt-1">
                  <p className="text-3xl font-bold text-sky-600" style={{ fontFamily: 'Outfit, sans-serif' }}>Rs. 299</p>
                </div>
                <p className="text-xs text-slate-500">/ month</p>
                <p className="text-[9px] text-amber-600 font-bold mt-1">Costs less than a cup of coffee a day</p>
              </div>
              <div className="space-y-3">
                <p className="text-[9px] font-bold text-emerald-600 uppercase tracking-wider">All Gold features, plus:</p>
                {PREMIUM_PLAN_FEATURES.map(({ icon: Icon, title, desc }) => {
                  const isComingSoon = desc?.includes('Coming Soon');
                  return (
                    <div key={title} className={`flex items-start gap-2 ${isComingSoon ? 'opacity-60' : ''}`}>
                      <Crown size={14} className="text-amber-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-xs font-semibold text-slate-900">
                          {title}
                          {isComingSoon && <span className="ml-1 px-1 py-0.5 rounded bg-violet-100 text-violet-600 text-[7px] font-bold">SOON</span>}
                        </p>
                        <p className="text-[9px] text-slate-500">{desc?.replace('Coming Soon — ', '')}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-5 pt-4 border-t border-sky-200 text-center">
                <Link to="/register?as=doctor&from=brochure" className="inline-flex items-center gap-1.5 px-6 py-2.5 rounded-full bg-sky-600 text-white text-xs font-bold hover:bg-sky-700 transition-colors">
                  Upgrade to Coffee Premium <ArrowRight size={14} />
                </Link>
              </div>

              {/* TurboCharge upsell — fills column space & drives awareness */}
              <div className="mt-5 pt-4 border-t border-sky-100">
                <div className="rounded-xl bg-gradient-to-br from-sky-600 to-emerald-500 p-4 text-white">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <Zap size={12} className="text-amber-200" />
                    <p className="text-[9px] font-bold uppercase tracking-widest text-sky-100">There's More</p>
                  </div>
                  <p className="text-sm font-semibold leading-snug mb-1.5" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Ready to dominate local search?
                  </p>
                  <p className="text-[10px] text-sky-50/95 leading-relaxed mb-3">
                    Everything in Coffee Premium + AI SEO articles you own + Clinic QR Kit + Visibility Accelerator + auto-generated landing pages. Your skills drive your ranking — not ad spend.
                  </p>
                  <a href="#turbocharge" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white text-sky-700 text-[11px] font-bold hover:bg-sky-50 transition-colors w-full justify-center" data-testid="premium-to-turbo-cta">
                    Go Turbo on Visibility — the ethical way <ArrowRight size={12} />
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 text-center">
            <p className="text-[10px] text-slate-400 mt-2 max-w-2xl mx-auto leading-relaxed">
              Pricing data is based on publicly available information from company websites, app stores, and industry reports as of 2026. Actual pricing may vary by plan, region, and negotiation. Company names have been anonymized. DocSite does not claim any affiliation with the companies referenced above.
            </p>
          </div>

          {/* Pricing highlight */}
          <div className="mt-16 bg-gradient-to-br from-sky-50 via-white to-emerald-50 rounded-3xl border border-sky-100 p-8 md:p-12" data-testid="pricing-highlight">
            <div className="grid md:grid-cols-3 gap-8 items-center">
              <div className="md:col-span-2">
                <h3 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Free to Start. Premium When You're Ready.
                </h3>
                <p className="text-sm text-slate-600 mt-3 leading-relaxed">
                  Every doctor gets a free website with scheduling — Gold tier is free for the first 200 doctors and Rs. 99/month thereafter. Upgrade to Coffee Premium for AI procedure descriptions, case studies, encrypted data export, and more.
                </p>
                <div className="flex flex-wrap gap-6 mt-6">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider font-medium">Gold · First 200 Doctors</p>
                    <p className="text-3xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Rs. 0</p>
                    <p className="text-xs text-slate-500">Website + Scheduling · Rs. 99/mo after</p>
                  </div>
                  <div className="border-l border-slate-200 pl-6">
                    <p className="text-xs text-sky-600 uppercase tracking-wider font-bold">Coffee Premium</p>
                    <div className="flex items-baseline gap-2 mt-1">
                      <p className="text-3xl font-bold text-sky-600" style={{ fontFamily: 'Outfit, sans-serif' }}>Rs. 299<span className="text-base font-normal text-slate-400">/mo</span></p>
                    </div>
                    <p className="text-xs text-slate-500">All features unlocked</p>
                    <span className="inline-flex items-center gap-1 mt-2 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-semibold border border-emerald-200" data-testid="cancel-anytime-badge">
                      <CheckCircle size={10} /> Cancel anytime · no lock-in
                    </span>
                  </div>
                </div>
              </div>
              <div className="text-center">
                <Link to="/register?as=doctor&from=brochure" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-sky-600 text-white font-semibold text-sm hover:bg-sky-700 transition-colors" data-testid="brochure-cta-register-2">
                  Get Started Free <ArrowRight size={16} />
                </Link>
                <p className="text-[10px] text-slate-400 mt-2">No credit card required</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ====== TURBOCHARGE YOUR VISIBILITY ====== */}
      <section id="turbocharge" className="bg-gradient-to-br from-sky-50 via-white to-emerald-50/30 py-16 md:py-24 scroll-mt-20" data-testid="brochure-turbocharge">
        <div className="w-full px-6 md:px-12 lg:px-20 xl:px-28">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-sky-100 border border-sky-200 text-sky-700 text-xs font-bold mb-4">
              <Zap size={14} /> TURBOCHARGE YOUR VISIBILITY
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Your Clinic. Your Patient. Your Own SEO Engine.<br />
              <span className="text-sky-600">DocSite Handles the Rest.</span>
            </h2>
            <p className="text-slate-600 mt-4 max-w-2xl mx-auto text-sm leading-relaxed">
              AI-written SEO articles you own. Printable QR poster for your clinic. Per-procedure landing pages. Turbo Visibility Accelerator with guided link-building.
              Auto-generated specialty+city pages that rank for local searches. Patient referral tracking. Your real expertise drives visibility — not ad spend.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
            {TURBO_FEATURES.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-md hover:border-sky-200 transition-all">
                <div className="w-10 h-10 rounded-xl bg-sky-50 flex items-center justify-center mb-3">
                  <Icon size={20} className="text-sky-600" />
                </div>
                <h3 className="text-sm font-semibold text-slate-900 mb-1">{title}</h3>
                <p className="text-xs text-slate-600 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>

          <div className="bg-gradient-to-r from-sky-50 to-emerald-50 rounded-3xl border border-sky-200 p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  TurboCharge Pack
                </h3>
                <p className="text-sm text-slate-600 mt-3 leading-relaxed">
                  Everything in Gold + Coffee Premium, plus the full AI SEO engine, per-procedure landing pages,
                  the <b>Clinic Kit</b>, <b>Visibility Accelerator</b> with guided backlink building,
                  auto-generated specialty+city pages, and patient referral tracking.
                  Your medical expertise becomes your visibility. Skills speak, not ad spend.
                </p>
                <div className="flex items-baseline gap-3 mt-6">
                  <p className="text-4xl font-bold text-sky-600" style={{ fontFamily: 'Outfit, sans-serif' }}>Rs. 999</p>
                  <span className="text-base text-slate-500">/month</span>
                </div>
                <p className="text-xs text-slate-500 mt-3">Continue with Gold (Free for the first 200 doctors) forever, even without TurboCharge.</p>
                <span className="inline-flex items-center gap-1 mt-3 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-semibold border border-emerald-200" data-testid="turbo-cancel-anytime-badge">
                  <CheckCircle size={11} /> Cancel anytime · no lock-in
                </span>
              </div>
              <div className="text-center">
                <Link to="/register?as=doctor&from=brochure" className="inline-flex items-center gap-2 px-10 py-4 rounded-full bg-sky-600 text-white font-semibold hover:bg-sky-700 transition-colors" data-testid="brochure-turbo-cta">
                  TurboCharge Your Visibility <ArrowRight size={18} />
                </Link>
                <p className="text-[10px] text-slate-500 mt-2">No credit card required to start with Gold</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ====== SECTION 3: PATIENT ADVANTAGES ====== */}
      <section id="for-patients" className="bg-slate-50 py-16 md:py-20 scroll-mt-20" data-testid="brochure-patient-benefits">
        <div className="w-full px-6 md:px-12 lg:px-20 xl:px-28">
          <div className="text-center mb-12">
            <p className="text-emerald-600 text-xs font-bold uppercase tracking-widest mb-3">For Patients</p>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Get Connected Forever with Doctors You Trust
            </h2>
            <p className="text-slate-500 mt-3 max-w-xl mx-auto text-sm">
              No middlemen. No commissions. Direct access to your doctor's website, schedule, and appointments.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {PATIENT_BENEFITS.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-white rounded-2xl border border-slate-200 p-5 text-center hover:shadow-md transition-shadow">
                <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                  <Icon size={22} className="text-emerald-600" />
                </div>
                <h3 className="text-sm font-semibold text-slate-900 mb-1">{title}</h3>
                <p className="text-xs text-slate-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>

          {/* Data security value prop */}
          <div className="mt-10 bg-white rounded-2xl border border-emerald-200 p-6 md:p-8 flex items-start gap-5">
            <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
              <Lock size={22} className="text-emerald-600" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-900">You Own Your Data</h3>
              <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                In these interactions through this site, there are no personal health records or clinical data being shared.
                In the event of loss of internet connectivity, your doctor still has offline interaction (non-health) data available
                which is securely stored encrypted on the servers. You can also keep a copy of your interaction history with you, offline.
                No vendor lock-in. No data held hostage. Encrypted to global standards.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ====== SECTION: SECURITY & TRUST ====== */}
      <section id="security" className="bg-white py-16 md:py-20 scroll-mt-20" data-testid="brochure-security">
        <div className="w-full px-6 md:px-12 lg:px-20 xl:px-28">
          <div className="text-center mb-12">
            <p className="text-violet-600 text-xs font-bold uppercase tracking-widest mb-3">Security & Trust</p>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Enterprise-Grade Security. Doctor-Simple UX.
            </h2>
            <p className="text-slate-500 mt-3 max-w-xl mx-auto text-sm">
              Every layer of DocSite is built with security-first principles. Your data, your patients, your reputation — protected.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: KeyRound, title: 'SMS OTP Verification', desc: 'Every doctor verifies their phone via real SMS OTP (MSG91). No fake accounts, no impersonation.', color: 'violet' },
              { icon: ShieldCheck, title: 'Two-Factor Authentication', desc: 'Doctor login requires password + live OTP to their registered phone. Even if password leaks, account stays safe.', color: 'violet' },
              { icon: Shield, title: 'Rate Limiting & Lockout', desc: '5 failed login attempts triggers a 15-minute lockout. Brute-force attacks are stopped cold.', color: 'violet' },
              { icon: Mail, title: 'Real-Time Email Alerts', desc: 'Admin gets instant email on new payments. Doctors get receipts. Subscription reminders auto-sent before expiry.', color: 'violet' },
              { icon: Bell, title: 'Admin Payment Notifications', desc: 'Every UPI payment submission triggers an instant admin notification. No payment goes unnoticed.', color: 'violet' },
              { icon: Lock, title: 'Field-Level Encrypted Export', desc: 'Doctors export interaction data encrypted with Fernet (AES-128 + HMAC-SHA256), sealed with their own passphrase. Patients can download their own interaction history anytime.', color: 'violet' },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-violet-50/30 rounded-2xl border border-violet-100 p-5 hover:shadow-md transition-shadow">
                <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center mb-3">
                  <Icon size={20} className="text-violet-600" />
                </div>
                <h3 className="text-sm font-semibold text-slate-900 mb-1">{title}</h3>
                <p className="text-xs text-slate-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ====== SECTION 4: MADE IN INDIA + CREDITS ====== */}
      <section className="bg-slate-50 py-16 md:py-20 border-t border-slate-200" data-testid="brochure-credits">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <div className="mb-8">
            <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-red-50 border border-red-100 text-slate-700 text-sm font-medium mb-6">
              <Heart size={16} className="text-red-400" /> Made with love in India
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              A standalone product from<br />
              <span className="text-sky-600">ChainX Tech Labs</span>
            </h2>
            <p className="text-slate-600 mt-4 max-w-lg mx-auto text-sm leading-relaxed">
              DocSite is an independent, purpose-built platform designed exclusively for doctors who want to own their digital identity. No aggregator middlemen. No commission traps. Just your practice, online.
            </p>
          </div>

          <div className="grid grid-cols-3 gap-6 mt-10 max-w-md mx-auto">
            <div>
              <p className="text-2xl font-bold text-sky-600" style={{ fontFamily: 'Outfit, sans-serif' }}>50+</p>
              <p className="text-xs text-slate-500 mt-1">Procedures</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-emerald-600" style={{ fontFamily: 'Outfit, sans-serif' }}>30</p>
              <p className="text-xs text-slate-500 mt-1">Specialties</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-600" style={{ fontFamily: 'Outfit, sans-serif' }}>PWA</p>
              <p className="text-xs text-slate-500 mt-1">Offline Ready</p>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-slate-200">
            <div className="flex items-center justify-center gap-3 mb-3">
              <img src="/docsite-logo.png" alt="DocSite" className="w-14 h-14 object-contain" />
              <div className="text-left">
                <p className="text-slate-900 font-semibold text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>DocSite</p>
                <p className="text-slate-500 text-[10px]">by ChainX Tech Labs</p>
              </div>
            </div>
            <p className="text-slate-500 text-xs">
              Intellectual Property of ChainX Tech Labs. All rights reserved.
            </p>
            <p className="text-slate-400 text-[10px] mt-2">
              Designed and engineered in India for the world.
            </p>
          </div>

          <div className="mt-10">
            <Link to="/register?as=doctor&from=brochure" className="inline-flex items-center gap-2 px-10 py-4 rounded-full bg-sky-600 text-white font-semibold hover:bg-sky-700 transition-colors" data-testid="brochure-cta-final">
              Launch Your Doctor Website <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
